import { Loader } from "@/components/Loader/Loader";
import { routePaths } from "@/config";
import { useAppDispatch } from "@/store";
import { updateUserConnected } from "@/store/reducers/user/user.action";
import { setAuthToken } from "@/utils/setAuthToken";
import { ToastError, ToastGeneric } from "@/utils/toast";
import { InlineIcon } from "@iconify/react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { apiLogin, apiResetPasswordSendMail } from "../../api/auth.api";
import ResetPasswordForm from "../ResetPasswordForm/ResetPasswordForm";
import "./SigninForm.styles.scss";

export type TUserFormsValues = {
  email: string;
  mot_passe: string;
};

const SigninForm: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const [refreshPasswordSuccessFull, setRefreshPasswordSuccessFull] =
    useState(false);
  const dispatch = useAppDispatch();
  const [currentIdUser, setCurrentIdUser] = useState(0);

  const validationSchema: any = Yup.object({
    mot_passe: Yup.string().required("Le mot de passe est requis !"),
    email: Yup.string()
      .email("L'email n'est pas valide !")
      .required("veuillez renseigner un email valide !"),
  });

  const formik = useFormik({
    initialValues: {
      mot_passe: "",
      email: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (value) => {
      console.log(value);
      setIsLoading(true);
      const requestLogin = {
        mot_passe: value.mot_passe.trim(),
        email: value.email.trim(),
      };
      apiLogin(requestLogin)
        .then((response) => {
          setIsLoading(false);

          if (response?.error) {
            ToastError.fire({
              text: response.message,
              timer: 9000,
            });
          } else {
            setIsFirstLogin(false);
            if (response.data && !response.data?.is_actif) {
              sendMailForResetPassword();
              setIsFirstLogin(true);
              setCurrentIdUser(response.data.user_id);
              return;
            } else {
              if (response.data && response.data?.profil_id) {
                response.data.profil = {
                  ...response.data?.profil_id,
                };
              }

              dispatch(updateUserConnected(response.data));
              setAuthToken(response.data?.token);
              navigate(routePaths.profile, { replace: true });
              ToastGeneric(
                `Bienvenue sur Reporting Finance.`,
                "success",
                9000
              ).fire();
            }
          }
        })
        .catch((error) => {
          console.log(error);

          if (error?.response?.data?.message) {
            ToastError.fire({
              text: error?.response?.data?.message,
              timer: 9000,
            });
          } else {
            ToastError.fire({ text: error, timer: 9000 });
          }

          setIsLoading(false);
        });
    },
  });

  const sendMailForResetPassword = () => {
    apiResetPasswordSendMail({
      email: formik.values.email.trim(),
    }).then((response) => {
      if (!response.error) {
        ToastGeneric(
          "Veuillez recupérer le code que nous avons envoyé à l'adresse " +
            formik.values.email,
          "success",
          3000
        ).fire();
      }
    });
  };

  useEffect(() => {
    formik.setValues({
      mot_passe: "",
      email: "",
    });
  }, [refreshPasswordSuccessFull]);

  return (
    <>
      {!isFirstLogin && (
        <div className="signin-container">
          <div className="header-part">
            <h1 className="title">REPORTING CDC-CI</h1>
            <p className="subtitle"> ACCÉDER À MON ESPACE </p>
          </div>
          <div className="form-part">
            <Loader isLoading={isLoading} />

            <form className="form-utilisateur" onSubmit={formik.handleSubmit}>
              <TextField
                placeholder="Votre addresse email"
                variant="outlined"
                label="Email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                type="text"
              />

              {formik.touched.email && formik.errors.email && (
                <small className="helper-text">
                  {" "}
                  <InlineIcon icon="ci:error-outline" /> {formik.errors.email}{" "}
                </small>
              )}

              <TextField
                placeholder="Votre mot de passe"
                variant="outlined"
                label="Mot de passe"
                type={showPassword ? "text" : "password"}
                name="mot_passe"
                value={formik.values.mot_passe}
                onChange={formik.handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ marginTop: "15px" }}
              />
              {formik.touched.mot_passe && formik.errors.mot_passe && (
                <small className="helper-text">
                  <InlineIcon icon="ci:error-outline" />{" "}
                  {formik.errors.mot_passe}{" "}
                </small>
              )}

              <Button
                variant="contained"
                color="tertiary"
                sx={{ marginTop: "30px" }}
                type="submit"
              >
                SE CONNECTER
              </Button>
            </form>
          </div>
          <div className="footer-part">
            <Link
              className="link-primary"
              to={routePaths.authChildren.forgottenPasswordAbsolute}
            >
              Mot de passe oublié ?
            </Link>
          </div>
        </div>
      )}

      {isFirstLogin && (
        <ResetPasswordForm
          idUser={currentIdUser}
          setIsFirstLogin={setIsFirstLogin}
          setRefreshPasswordSuccessFull={setRefreshPasswordSuccessFull}
        />
      )}
    </>
  );
};

export default SigninForm;
